import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { tenantProfileService } from "../services/tenantProfileService";
import {
  TenantProfileValuesInit,
  getTenantProfileCreateVariables,
  getTenantProfileValues,
  TenantProfileValuesType,
  TenantProfileErrorsType,
  tenantProfileDisplayId,
} from "./TenantProfileCommonStore";
import { TenantProfile, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getPublisherValues,
  PublisherValuesType,
} from "../../publisher/store/PublisherCommonStore";
import { validateMask } from "../../common/utils";
import { normalizeAndValidateEmail } from "../../../common/utils";
import {
  getCountryValues,
  CountryValuesType,
} from "../../country/store/CountryCommonStore";
import {
  getProvinceStateValues,
  ProvinceStateValuesType,
} from "../../provinceState/store/ProvinceStateCommonStore";
import {
  getVatStatusValues,
  VatStatusValuesType,
} from "../../vatStatus/store/VatStatusCommonStore";
import {
  getGrossReceiptsStatusValues,
  GrossReceiptsStatusValuesType,
} from "../../grossReceiptsStatus/store/GrossReceiptsStatusCommonStore";
import {
  getCurrencyValues,
  CurrencyValuesType,
} from "../../currency/store/CurrencyCommonStore";
import { PersonType } from "../../../api/graphql";
import { publisherService } from "../../publisher/services/publisherService";
import { countryService } from "../../country/services/countryService";
import { provinceStateService } from "../../provinceState/services/provinceStateService";
import { vatStatusService } from "../../vatStatus/services/vatStatusService";
import { grossReceiptsStatusService } from "../../grossReceiptsStatus/services/grossReceiptsStatusService";
import { currencyService } from "../../currency/services/currencyService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class TenantProfileCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: TenantProfileErrorsType = {};
  service = tenantProfileService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    business: { open: true, errors: [] },
    address: { open: true, errors: [] },
    billing: { open: true, errors: [] },
    printer: { open: true, errors: [] },
    integrationsSadaic: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: TenantProfile | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = TenantProfileValuesInit();
  personTypeError: string | undefined;
  legalNameError: string | undefined;
  ownPublisherError: string | undefined;
  ownPublisherService = publisherService;
  ownPublisherSearchValue: string = "";
  ownPublisherOptions: PublisherValuesType[] = [];
  businessIdError: string | undefined;
  legalEmailError: string | undefined;
  streetAndNumberError: string | undefined;
  floorError: string | undefined;
  suiteError: string | undefined;
  postalCodeError: string | undefined;
  countryError: string | undefined;
  countryService = countryService;
  countrySearchValue: string = "";
  countryOptions: CountryValuesType[] = [];
  provinceError: string | undefined;
  provinceService = provinceStateService;
  provinceSearchValue: string = "";
  provinceOptions: ProvinceStateValuesType[] = [];
  cityError: string | undefined;
  vatStatusError: string | undefined;
  vatStatusService = vatStatusService;
  vatStatusSearchValue: string = "";
  vatStatusOptions: VatStatusValuesType[] = [];
  taxIdError: string | undefined;
  grossReceiptsStatusError: string | undefined;
  grossReceiptsStatusService = grossReceiptsStatusService;
  grossReceiptsStatusSearchValue: string = "";
  grossReceiptsStatusOptions: GrossReceiptsStatusValuesType[] = [];
  grossReceiptsIdError: string | undefined;
  billingEmailError: string | undefined;
  billingCurrencyError: string | undefined;
  billingCurrencyService = currencyService;
  billingCurrencySearchValue: string = "";
  billingCurrencyOptions: CurrencyValuesType[] = [];
  printerNameError: string | undefined;
  printerStreetAndNumberError: string | undefined;
  printerFloorError: string | undefined;
  printerSuiteError: string | undefined;
  printerPostalCodeError: string | undefined;
  printerCountryError: string | undefined;
  printerCountryService = countryService;
  printerCountrySearchValue: string = "";
  printerCountryOptions: CountryValuesType[] = [];
  printerProvinceError: string | undefined;
  printerProvinceService = provinceStateService;
  printerProvinceSearchValue: string = "";
  printerProvinceOptions: ProvinceStateValuesType[] = [];
  printerCityError: string | undefined;
  sadaicFtpUrlError: string | undefined;
  sadaicFtpUserError: string | undefined;
  sadaicFtpPasswordError: string | undefined;
  sadaicEmailError: string | undefined;

  private customValidators: CustomValidator<TenantProfileValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<TenantProfileValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<TenantProfileValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return tenantProfileDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantProfileSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity

      if (params.callerData?.fieldName === "ownPublisher") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showOwnPublisher = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.publisherOne({
            id: params.returnData.resultId,
          });
          const publisher = result.publisherOne;
          this.entityValues.showOwnPublisher = {
            displayId: publisher.displayId,
            id: publisher.id,
          };
          this.setOwnPublisherOptions([
            getPublisherValues(publisher, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "country") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showCountry = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({
            id: params.returnData.resultId,
          });
          const country = result.countryOne;
          this.entityValues.showCountry = {
            displayId: country.displayId,
            id: country.id,
          };
          this.setCountryOptions([getCountryValues(country, this.langStore)]);
        }
      } else if (params.callerData?.fieldName === "province") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showProvince = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.provinceStateOne({
            id: params.returnData.resultId,
          });
          const provinceState = result.provinceStateOne;
          this.entityValues.showProvince = {
            displayId: provinceState.displayId,
            id: provinceState.id,
          };
          this.setProvinceOptions([
            getProvinceStateValues(provinceState, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "vatStatus") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showVatStatus = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.vatStatusOne({
            id: params.returnData.resultId,
          });
          const vatStatus = result.vatStatusOne;
          this.entityValues.showVatStatus = {
            displayId: vatStatus.displayId,
            id: vatStatus.id,
          };
          this.setVatStatusOptions([
            getVatStatusValues(vatStatus, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "grossReceiptsStatus") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showGrossReceiptsStatus = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.grossReceiptsStatusOne({
            id: params.returnData.resultId,
          });
          const grossReceiptsStatus = result.grossReceiptsStatusOne;
          this.entityValues.showGrossReceiptsStatus = {
            displayId: grossReceiptsStatus.displayId,
            id: grossReceiptsStatus.id,
          };
          this.setGrossReceiptsStatusOptions([
            getGrossReceiptsStatusValues(grossReceiptsStatus, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "billingCurrency") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showBillingCurrency = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.currencyOne({
            id: params.returnData.resultId,
          });
          const currency = result.currencyOne;
          this.entityValues.showBillingCurrency = {
            displayId: currency.displayId,
            id: currency.id,
          };
          this.setBillingCurrencyOptions([
            getCurrencyValues(currency, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "printerCountry") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showPrinterCountry = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({
            id: params.returnData.resultId,
          });
          const country = result.countryOne;
          this.entityValues.showPrinterCountry = {
            displayId: country.displayId,
            id: country.id,
          };
          this.setPrinterCountryOptions([
            getCountryValues(country, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "printerProvince") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showPrinterProvince = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.provinceStateOne({
            id: params.returnData.resultId,
          });
          const provinceState = result.provinceStateOne;
          this.entityValues.showPrinterProvince = {
            displayId: provinceState.displayId,
            id: provinceState.id,
          };
          this.setPrinterProvinceOptions([
            getProvinceStateValues(provinceState, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "business",
        label: this.rootStore.langStore.groupTenantProfileBusinessLabel,
        errors: this.getCardErrors("business"),
      },
      {
        id: "address",
        label: this.rootStore.langStore.groupTenantProfileAddressLabel,
        errors: this.getCardErrors("address"),
      },
      {
        id: "billing",
        label: this.rootStore.langStore.groupTenantProfileBillingLabel,
        errors: this.getCardErrors("billing"),
      },
      {
        id: "printer",
        label: this.rootStore.langStore.groupTenantProfilePrinterLabel,
        errors: this.getCardErrors("printer"),
      },
      {
        id: "integrationsSadaic",
        label:
          this.rootStore.langStore.groupTenantProfileIntegrationsSadaicLabel,
        errors: this.getCardErrors("integrationsSadaic"),
      },
    ];
  }

  getPersonTypeValue(): PersonType | "" {
    return this.entityValues.personType;
  }

  setPersonTypeValue(value: PersonType | "") {
    this.entityValues.personType = value;
  }

  getPersonTypeOptions() {
    return this.langStore.PersonTypeOptions;
  }

  getLegalNameValue(): string {
    return this.entityValues.legalName;
  }

  setLegalNameValue(value: string) {
    this.entityValues.legalName = value;
  }

  setOwnPublisherOptions(values: PublisherValuesType[]) {
    this.ownPublisherOptions = values;
  }

  *loadOwnPublisherValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Publisher");
    const allOptions = cache.getOptions("Publisher") as PublisherValuesType[];

    const searchValue = this.ownPublisherSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setOwnPublisherOptions(filtered);
  }

  getOwnPublisherOptions() {
    return this.ownPublisherOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setOwnPublisherValue(value: string) {
    this.ownPublisherSearchValue = value;
    yield this.loadOwnPublisherValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedOwnPublisher(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewPublisher(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "ownPublisher"
      );
    } else {
      if (value) {
        this.entityValues.showOwnPublisher = value;
        this.entityValues.__ownPublisher = value.displayId;
      }
    }
  }

  getBusinessIdValue(): string {
    return this.entityValues.businessId;
  }

  setBusinessIdValue(value: string) {
    this.entityValues.businessId = value;
  }

  getLegalEmailValue(): string {
    return this.entityValues.legalEmail;
  }

  setLegalEmailValue(value: string) {
    this.entityValues.legalEmail = value;
  }

  getStreetAndNumberValue(): string {
    return this.entityValues.streetAndNumber;
  }

  setStreetAndNumberValue(value: string) {
    this.entityValues.streetAndNumber = value;
  }

  getFloorValue(): string {
    return this.entityValues.floor;
  }

  setFloorValue(value: string) {
    this.entityValues.floor = value;
  }

  getSuiteValue(): string {
    return this.entityValues.suite;
  }

  setSuiteValue(value: string) {
    this.entityValues.suite = value;
  }

  getPostalCodeValue(): string {
    return this.entityValues.postalCode;
  }

  setPostalCodeValue(value: string) {
    this.entityValues.postalCode = value;
  }

  setCountryOptions(values: CountryValuesType[]) {
    this.countryOptions = values;
  }

  *loadCountryValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = cache.getOptions("Country") as CountryValuesType[];

    const searchValue = this.countrySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setCountryOptions(filtered);
  }

  getCountryOptions() {
    return this.countryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setCountryValue(value: string) {
    this.countrySearchValue = value;
    yield this.loadCountryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedCountry(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "country"
      );
    } else {
      if (value) {
        this.entityValues.showCountry = value;
        this.entityValues.__country = value.displayId;
        yield this.loadProvinceValues();
        yield this.loadPrinterProvinceValues();
        this.entityValues.showProvince = null;
        this.entityValues.__province = "";
        this.entityValues.showPrinterProvince = null;
        this.entityValues.__printerProvince = "";
      }
    }
  }

  setProvinceOptions(values: ProvinceStateValuesType[]) {
    this.provinceOptions = values;
  }

  *loadProvinceValues(): any {
    const parentId = this.entityValues.showCountry?.id;
    if (parentId) {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.provinceStateListWithOffset({
        offset: 0,
        take: 100,
        where: { country: { id: { equals: parentId } } },
        orderBy: [{ [this.langStore.fieldByLang("name")]: SortOrder.Asc }],
      });
      this.setProvinceOptions(
        (result.provinceStateListWithOffset || []).map((x: any) =>
          getProvinceStateValues(x, this.langStore)
        )
      );
    } else {
      this.setProvinceOptions([]);
    }
  }

  getProvinceOptions() {
    return this.provinceOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setProvinceValue(value: string) {
    this.provinceSearchValue = value;
    yield this.loadProvinceValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedProvince(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewProvinceState(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "province"
      );
    } else {
      if (value) {
        this.entityValues.showProvince = value;
        this.entityValues.__province = value.displayId;
      }
    }
  }

  getCityValue(): string {
    return this.entityValues.city;
  }

  setCityValue(value: string) {
    this.entityValues.city = value;
  }

  setVatStatusOptions(values: VatStatusValuesType[]) {
    this.vatStatusOptions = values;
  }

  *loadVatStatusValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("VatStatus");
    const allOptions = cache.getOptions("VatStatus") as VatStatusValuesType[];

    const searchValue = this.vatStatusSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setVatStatusOptions(filtered);
  }

  getVatStatusOptions() {
    return this.vatStatusOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setVatStatusValue(value: string) {
    this.vatStatusSearchValue = value;
    yield this.loadVatStatusValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedVatStatus(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewVatStatus(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "vatStatus"
      );
    } else {
      if (value) {
        this.entityValues.showVatStatus = value;
        this.entityValues.__vatStatus = value.displayId;
      }
    }
  }

  getTaxIdValue(): string {
    return this.entityValues.taxId;
  }

  setTaxIdValue(value: string) {
    this.entityValues.taxId = value;
  }

  setGrossReceiptsStatusOptions(values: GrossReceiptsStatusValuesType[]) {
    this.grossReceiptsStatusOptions = values;
  }

  *loadGrossReceiptsStatusValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("GrossReceiptsStatus");
    const allOptions = cache.getOptions(
      "GrossReceiptsStatus"
    ) as GrossReceiptsStatusValuesType[];

    const searchValue =
      this.grossReceiptsStatusSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setGrossReceiptsStatusOptions(filtered);
  }

  getGrossReceiptsStatusOptions() {
    return this.grossReceiptsStatusOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setGrossReceiptsStatusValue(value: string) {
    this.grossReceiptsStatusSearchValue = value;
    yield this.loadGrossReceiptsStatusValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedGrossReceiptsStatus(
    value: { displayId: string; id: string } | null
  ) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewGrossReceiptsStatus(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "grossReceiptsStatus"
      );
    } else {
      if (value) {
        this.entityValues.showGrossReceiptsStatus = value;
        this.entityValues.__grossReceiptsStatus = value.displayId;
      }
    }
  }

  getGrossReceiptsIdValue(): string {
    return this.entityValues.grossReceiptsId;
  }

  setGrossReceiptsIdValue(value: string) {
    this.entityValues.grossReceiptsId = value;
  }

  getBillingEmailValue(): string {
    return this.entityValues.billingEmail;
  }

  setBillingEmailValue(value: string) {
    this.entityValues.billingEmail = value;
  }

  setBillingCurrencyOptions(values: CurrencyValuesType[]) {
    this.billingCurrencyOptions = values;
  }

  *loadBillingCurrencyValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Currency");
    const allOptions = cache.getOptions("Currency") as CurrencyValuesType[];

    const searchValue = this.billingCurrencySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setBillingCurrencyOptions(filtered);
  }

  getBillingCurrencyOptions() {
    return this.billingCurrencyOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setBillingCurrencyValue(value: string) {
    this.billingCurrencySearchValue = value;
    yield this.loadBillingCurrencyValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedBillingCurrency(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCurrency(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "billingCurrency"
      );
    } else {
      if (value) {
        this.entityValues.showBillingCurrency = value;
        this.entityValues.__billingCurrency = value.displayId;
      }
    }
  }

  getPrinterNameValue(): string {
    return this.entityValues.printerName;
  }

  setPrinterNameValue(value: string) {
    this.entityValues.printerName = value;
  }

  getPrinterStreetAndNumberValue(): string {
    return this.entityValues.printerStreetAndNumber;
  }

  setPrinterStreetAndNumberValue(value: string) {
    this.entityValues.printerStreetAndNumber = value;
  }

  getPrinterFloorValue(): string {
    return this.entityValues.printerFloor;
  }

  setPrinterFloorValue(value: string) {
    this.entityValues.printerFloor = value;
  }

  getPrinterSuiteValue(): string {
    return this.entityValues.printerSuite;
  }

  setPrinterSuiteValue(value: string) {
    this.entityValues.printerSuite = value;
  }

  getPrinterPostalCodeValue(): string {
    return this.entityValues.printerPostalCode;
  }

  setPrinterPostalCodeValue(value: string) {
    this.entityValues.printerPostalCode = value;
  }

  setPrinterCountryOptions(values: CountryValuesType[]) {
    this.printerCountryOptions = values;
  }

  *loadPrinterCountryValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = cache.getOptions("Country") as CountryValuesType[];

    const searchValue = this.printerCountrySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setPrinterCountryOptions(filtered);
  }

  getPrinterCountryOptions() {
    return this.printerCountryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPrinterCountryValue(value: string) {
    this.printerCountrySearchValue = value;
    yield this.loadPrinterCountryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedPrinterCountry(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "printerCountry"
      );
    } else {
      if (value) {
        this.entityValues.showPrinterCountry = value;
        this.entityValues.__printerCountry = value.displayId;
      }
    }
  }

  setPrinterProvinceOptions(values: ProvinceStateValuesType[]) {
    this.printerProvinceOptions = values;
  }

  *loadPrinterProvinceValues(): any {
    const parentId = this.entityValues.showCountry?.id;
    if (parentId) {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.provinceStateListWithOffset({
        offset: 0,
        take: 100,
        where: { country: { id: { equals: parentId } } },
        orderBy: [{ [this.langStore.fieldByLang("name")]: SortOrder.Asc }],
      });
      this.setPrinterProvinceOptions(
        (result.provinceStateListWithOffset || []).map((x: any) =>
          getProvinceStateValues(x, this.langStore)
        )
      );
    } else {
      this.setPrinterProvinceOptions([]);
    }
  }

  getPrinterProvinceOptions() {
    return this.printerProvinceOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPrinterProvinceValue(value: string) {
    this.printerProvinceSearchValue = value;
    yield this.loadPrinterProvinceValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedPrinterProvince(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewProvinceState(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "printerProvince"
      );
    } else {
      if (value) {
        this.entityValues.showPrinterProvince = value;
        this.entityValues.__printerProvince = value.displayId;
      }
    }
  }

  getPrinterCityValue(): string {
    return this.entityValues.printerCity;
  }

  setPrinterCityValue(value: string) {
    this.entityValues.printerCity = value;
  }

  getSadaicFtpUrlValue(): string {
    return this.entityValues.sadaicFtpUrl;
  }

  setSadaicFtpUrlValue(value: string) {
    this.entityValues.sadaicFtpUrl = value;
  }

  getSadaicFtpUserValue(): string {
    return this.entityValues.sadaicFtpUser;
  }

  setSadaicFtpUserValue(value: string) {
    this.entityValues.sadaicFtpUser = value;
  }

  getSadaicFtpPasswordValue(): string {
    return this.entityValues.sadaicFtpPassword;
  }

  setSadaicFtpPasswordValue(value: string) {
    this.entityValues.sadaicFtpPassword = value;
  }

  getSadaicEmailValue(): string {
    return this.entityValues.sadaicEmail;
  }

  setSadaicEmailValue(value: string) {
    this.entityValues.sadaicEmail = value;
  }

  addNewPublisher(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/publisher/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewCountry(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/country/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewProvinceState(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/provinceState/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewVatStatus(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/vatStatus/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewGrossReceiptsStatus(
    notFound: string | undefined,
    callerFieldName?: string
  ) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/grossReceiptsStatus/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewCurrency(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/currency/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = TenantProfileValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      business: { open: true, errors: [] },
      address: { open: true, errors: [] },
      billing: { open: true, errors: [] },
      printer: { open: true, errors: [] },
      integrationsSadaic: { open: true, errors: [] },
    };
    this.ownPublisherSearchValue = "";
    this.ownPublisherOptions = [];
    this.countrySearchValue = "";
    this.countryOptions = [];
    this.provinceSearchValue = "";
    this.provinceOptions = [];
    this.vatStatusSearchValue = "";
    this.vatStatusOptions = [];
    this.grossReceiptsStatusSearchValue = "";
    this.grossReceiptsStatusOptions = [];
    this.billingCurrencySearchValue = "";
    this.billingCurrencyOptions = [];
    this.printerCountrySearchValue = "";
    this.printerCountryOptions = [];
    this.printerProvinceSearchValue = "";
    this.printerProvinceOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: TenantProfileValuesType): TenantProfileErrorsType {
    const errors: TenantProfileErrorsType = {};
    this.clearErrors();
    if (values.businessId && true) {
      if (
        !["99-9999999-9", "99-99999999-9"].some((m) =>
          validateMask(values.businessId, { mask: m })
        )
      ) {
        errors.businessId = {
          message: this.langStore.maskedField("99-9999999-9 | 99-99999999-9"),
          fieldLabel: this.langStore.TenantProfileBusinessIdLabel,
        };
      }
    }

    if (values.legalEmail) {
      const normalizedEmail = normalizeAndValidateEmail(values.legalEmail);
      if (normalizedEmail === null) {
        errors.legalEmail = {
          message: this.langStore.badSyntaxEmail,
          fieldLabel: this.langStore.TenantProfileLegalEmailLabel,
        };
        this.setCardError("business", errors.legalEmail.message);
      }
    }

    if (values.billingEmail) {
      const normalizedEmail = normalizeAndValidateEmail(values.billingEmail);
      if (normalizedEmail === null) {
        errors.billingEmail = {
          message: this.langStore.badSyntaxEmail,
          fieldLabel: this.langStore.TenantProfileBillingEmailLabel,
        };
        this.setCardError("billing", errors.billingEmail.message);
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "TenantProfileCreateStore"
    );
  }

  *duplicatesTenantProfile(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const tenantProfileInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (tenantProfileInstance) {
      this.entityValues = getTenantProfileValues(
        tenantProfileInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(
        `/tenantProfile/update/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewTenantProfile(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewTenantProfile(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      personType: this.entityValues.personType,
      legalName: this.entityValues.legalName,
      businessId: this.entityValues.businessId,
      legalEmail: this.entityValues.legalEmail,
      streetAndNumber: this.entityValues.streetAndNumber,
      floor: this.entityValues.floor,
      suite: this.entityValues.suite,
      postalCode: this.entityValues.postalCode,
      city: this.entityValues.city,
      taxId: this.entityValues.taxId,
      grossReceiptsId: this.entityValues.grossReceiptsId,
      billingEmail: this.entityValues.billingEmail,
      printerName: this.entityValues.printerName,
      printerStreetAndNumber: this.entityValues.printerStreetAndNumber,
      printerFloor: this.entityValues.printerFloor,
      printerSuite: this.entityValues.printerSuite,
      printerPostalCode: this.entityValues.printerPostalCode,
      printerCity: this.entityValues.printerCity,
      sadaicFtpUrl: this.entityValues.sadaicFtpUrl,
      sadaicFtpUser: this.entityValues.sadaicFtpUser,
      sadaicFtpPassword: this.entityValues.sadaicFtpPassword,
      sadaicEmail: this.entityValues.sadaicEmail,
      ownPublisher: this.entityValues.showOwnPublisher?.id || null,
      country: this.entityValues.showCountry?.id || null,
      province: this.entityValues.showProvince?.id || null,
      vatStatus: this.entityValues.showVatStatus?.id || null,
      grossReceiptsStatus:
        this.entityValues.showGrossReceiptsStatus?.id || null,
      billingCurrency: this.entityValues.showBillingCurrency?.id || null,
      printerCountry: this.entityValues.showPrinterCountry?.id || null,
      printerProvince: this.entityValues.showPrinterProvince?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getTenantProfileValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = TenantProfileValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getTenantProfileCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantProfileCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.tenantProfileCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.tenantProfileCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesTenantProfile(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.legalName = initialDisplayId;
      }

      yield this.loadOwnPublisherValues();
      yield this.loadCountryValues();
      yield this.loadProvinceValues();
      yield this.loadVatStatusValues();
      yield this.loadGrossReceiptsStatusValues();
      yield this.loadBillingCurrencyValues();
      yield this.loadPrinterCountryValues();
      yield this.loadPrinterProvinceValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
